import React, { useState, useEffect } from 'react';
import { Shield, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { isDemoAccount } from '../lib/auth';

export function ApplicationStatus() {
  const [status, setStatus] = useState<string>('pending');
  const [loading, setLoading] = useState(true);
  const [showStatus, setShowStatus] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [visible, setVisible] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    checkAuth();
    if (isAuthenticated) {
      checkStatus();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (showStatus && hasSubmitted) {
      const fadeTimer = setTimeout(() => {
        setFadeOut(true);
      }, 5000);

      const hideTimer = setTimeout(() => {
        setVisible(false);
      }, 5500);

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [showStatus, hasSubmitted]);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    } catch (error) {
      console.error('Error checking auth status:', error);
      setIsAuthenticated(false);
    }
  };

  const checkStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setShowStatus(false);
        return;
      }

      // Check if this is a demo account
      if (isDemoAccount(user.email)) {
        setShowStatus(false);
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('moderation_status, application_text')
        .eq('id', user.id)
        .single();

      if (profile?.application_text) {
        setHasSubmitted(true);
        setShowStatus(true);
        setStatus(profile.moderation_status);
      } else {
        setShowStatus(false);
      }
    } catch (error) {
      console.error('Error checking status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !showStatus || !hasSubmitted || !isAuthenticated || !visible) return null;

  const statusConfig = {
    pending: {
      icon: Clock,
      color: 'text-yellow-600',
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      title: 'Application Under Review',
      message: 'We\'re reviewing your application. This usually takes 24-48 hours.'
    },
    approved: {
      icon: CheckCircle,
      color: 'text-green-600',
      bg: 'bg-green-50',
      border: 'border-green-200',
      title: 'Welcome to the Community!',
      message: 'Your application has been approved. You now have full access to all features.'
    },
    rejected: {
      icon: XCircle,
      color: 'text-red-600',
      bg: 'bg-red-50',
      border: 'border-red-200',
      title: 'Application Not Approved',
      message: 'Unfortunately, we cannot approve your application at this time.'
    },
    flagged: {
      icon: AlertTriangle,
      color: 'text-orange-600',
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      title: 'Additional Review Needed',
      message: 'We need some additional information to process your application.'
    }
  };

  const config = statusConfig[status as keyof typeof statusConfig];
  if (!config) return null;

  const Icon = config.icon;

  return (
    <div 
      className={`fixed top-4 right-4 max-w-sm p-3 md:p-4 rounded-lg border z-40 ${
        config.bg
      } ${config.border} transition-opacity duration-500 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}
    >
      <div className="flex items-start">
        <div className={`${config.color} mr-3`}>
          <Icon className="w-5 h-5 md:w-6 md:h-6" />
        </div>
        <div>
          <h3 className={`text-sm font-medium ${config.color}`}>
            {config.title}
          </h3>
          <p className="mt-1 text-xs md:text-sm text-gray-600">
            {config.message}
          </p>
        </div>
      </div>
    </div>
  );
}